import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import DojoShell from "@/components/DojoShell";
import { useAuth } from "@/context/AuthContext";
import { getGate, getFormation } from "@/lib/api";
import { biomeFor } from "@/lib/biomes";

export default function Porte() {
  const { gid } = useParams();
  const nav = useNavigate();
  const { loading, isAuthenticated } = useAuth();
  const [gate, setGate] = useState(null);
  const [formation, setFormation] = useState(null);

  useEffect(() => {
    if (!loading && !isAuthenticated) nav("/connexion", { replace: true, state: { from: { pathname: `/ecole/porte/${gid}` } } });
  }, [loading, isAuthenticated, nav, gid]);

  useEffect(() => {
    getGate(gid).then((g) => {
      setGate(g);
      if (g.formation_id) getFormation(g.formation_id).then(setFormation).catch(() => {});
    }).catch(() => toast("Porte introuvable"));
  }, [gid]);

  const b = biomeFor(gate?.biome || gate?.index);
  const particles = useMemo(() => Array.from({ length: 14 }).map((_, i) => ({
    i, x: Math.random() * 100, delay: (Math.random() * 4).toFixed(2),
    dur: (5 + Math.random() * 6).toFixed(2), size: (8 + Math.random() * 16).toFixed(0),
    drift: (Math.random() * 50 - 25).toFixed(0),
  })), [gate?.id]);

  if (!gate) return <DojoShell crumbs={[{ label: "École", to: "/ecole" }]}><p style={{ color: "var(--text-secondary)" }}>Ouverture de la porte…</p></DojoShell>;

  const crumbs = [
    { label: "École", to: "/ecole" },
    ...(formation ? [{ label: formation.title, to: `/ecole/formation/${formation.id}` }] : []),
    { label: gate.title },
  ];

  return (
    <DojoShell crumbs={crumbs}>
      {/* biome immersif : fond + particules */}
      <div style={{ ...p.biome, background: b.env }} aria-hidden>
        <div style={{ ...p.biomeGlow, background: `radial-gradient(circle at 50% 0%, ${b.color}44, transparent 60%)` }} />
        <div style={p.particles}>
          {particles.map((pt) => (
            <span key={pt.i} style={{
              position: "absolute", bottom: "-6%", left: `${pt.x}%`, fontSize: `${pt.size}px`,
              opacity: 0.5, filter: `drop-shadow(0 0 8px ${b.color})`,
              animation: `porteFloat ${pt.dur}s ease-in-out ${pt.delay}s infinite`,
            }}>{b.motif}</span>
          ))}
        </div>
      </div>

      <div style={p.wrap}>
        <button style={p.backBtn} data-testid="porte-back" onClick={() => formation ? nav(`/ecole/formation/${formation.id}`) : nav("/ecole")}>
          ← Retour aux Portes
        </button>
        <div style={p.header}>
          <span style={{ ...p.kanji, color: "#fff", textShadow: `0 0 40px ${b.glow}, 0 0 80px ${b.color}` }}>{gate.kanji}</span>
          <div style={{ flex: 1 }}>
            <div style={{ ...p.eyebrow, color: b.glow }}>Porte {gate.index} · {gate.element}</div>
            <h1 style={p.h1}>{gate.title}</h1>
            <p style={p.desc}>{gate.description}</p>
          </div>
          <div style={{ ...p.badge, borderColor: `${b.color}66`, background: `${b.color}22` }}>
            <span style={{ ...p.badgeKanji, color: b.glow }}>{gate.badge_kanji}</span>
            <span style={{ ...p.badgeLabel, color: b.glow }}>{gate.badge_label}</span>
          </div>
        </div>

        {(gate.modules || []).map((m) => (
          <div key={m.id} style={p.module}>
            <div style={{ ...p.mHead, borderLeft: `4px solid ${b.color}` }}>
              <span style={p.mTitle}>{m.title}</span>
              {m.description && <span style={p.mDesc}>{m.description}</span>}
            </div>
            <div style={p.lessons}>
              {(m.lessons || []).map((l, i) => (
                <button key={l.id} style={p.lesson} onClick={() => nav(`/ecole/lecon/${l.id}`)}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                  <span style={{ ...p.lNum, ...(l.completed ? { background: b.color, color: "#fff", borderColor: b.color } : {}) }}>{l.completed ? "✓" : i + 1}</span>
                  <div style={p.lInfo}>
                    <span style={p.lTitle}>{l.title}</span>
                    <span style={p.lMeta}>{l.duration || "Vidéo"} {l.locked_video ? "· 🔒 abonnés" : ""}</span>
                  </div>
                  <span style={{ ...p.lGo, color: b.glow }}>{l.completed ? "Revoir" : "Regarder →"}</span>
                </button>
              ))}
            </div>
            {(m.resources || []).length > 0 && (
              <div style={p.resWrap} data-testid={`module-resources-${m.id}`}>
                <div style={{ ...p.resHead, color: b.glow }}>📚 Ressources pour t'aider</div>
                <div style={p.resGrid}>
                  {m.resources.map((r) => (
                    <button key={r.id} style={p.resCard} data-testid={`module-resource-${r.id}`}
                      onClick={() => nav(`/bibliotheque/${r.id}`)}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = b.color; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.14)"; }}>
                      <span style={{ ...p.resType, background: `${b.color}30`, color: b.glow }}>{r.type || "Fiche"}</span>
                      <span style={p.resTitle}>{r.title}</span>
                      <span style={{ ...p.resGo, color: b.glow }}>Ouvrir dans la Bibliothèque →</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </DojoShell>
  );
}

const p = {
  biome: { position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none" },
  biomeGlow: { position: "absolute", inset: 0 },
  particles: { position: "absolute", inset: 0, overflow: "hidden" },
  wrap: { position: "relative", zIndex: 1 },
  backBtn: { display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.25)", background: "rgba(15,15,20,0.55)", backdropFilter: "blur(8px)", color: "#fff", fontWeight: 700, fontSize: 13.5, cursor: "pointer", marginBottom: 18 },
  header: { display: "flex", gap: 20, alignItems: "center", marginBottom: 28, flexWrap: "wrap" },
  kanji: { fontSize: 76, fontWeight: 800, lineHeight: 1 },
  eyebrow: { fontWeight: 800, fontSize: 12.5, textTransform: "uppercase", letterSpacing: 1.5 },
  h1: { fontSize: 32, fontWeight: 900, margin: "4px 0", color: "#fff", textShadow: "0 2px 20px rgba(0,0,0,0.5)" },
  desc: { color: "rgba(255,255,255,0.82)", fontSize: 15, margin: 0, maxWidth: 640, lineHeight: 1.6 },
  badge: { marginLeft: "auto", textAlign: "center", padding: "14px 18px", borderRadius: 16, border: "1.5px solid", backdropFilter: "blur(6px)" },
  badgeKanji: { display: "block", fontSize: 30, fontWeight: 800 },
  badgeLabel: { fontSize: 12, fontWeight: 700 },
  module: { marginBottom: 20, borderRadius: 18, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(15,15,20,0.55)", backdropFilter: "blur(12px)", overflow: "hidden", boxShadow: "0 16px 40px rgba(0,0,0,0.35)" },
  mHead: { padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.1)", display: "flex", flexDirection: "column", gap: 2 },
  mTitle: { fontWeight: 800, fontSize: 16.5, color: "#fff" },
  mDesc: { fontSize: 13, color: "rgba(255,255,255,0.6)" },
  lessons: { display: "flex", flexDirection: "column" },
  lesson: { display: "flex", alignItems: "center", gap: 16, padding: "14px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)", background: "transparent", border: "none", cursor: "pointer", textAlign: "left", width: "100%", transition: "background .2s ease" },
  lNum: { width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,0.1)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14, flexShrink: 0 },
  lInfo: { flex: 1, display: "flex", flexDirection: "column" },
  lTitle: { fontWeight: 700, fontSize: 15, color: "#fff" },
  lMeta: { fontSize: 12.5, color: "rgba(255,255,255,0.6)" },
  lGo: { fontWeight: 800, fontSize: 13.5, flexShrink: 0 },
  resWrap: { padding: "14px 20px 18px", borderTop: "1px solid rgba(255,255,255,0.08)", background: "rgba(0,0,0,0.15)" },
  resHead: { fontWeight: 800, fontSize: 13, letterSpacing: 0.4, marginBottom: 10, textTransform: "uppercase" },
  resGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 },
  resCard: { display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-start", padding: "12px 14px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.05)", cursor: "pointer", textAlign: "left", transition: "border-color .2s ease" },
  resType: { fontSize: 10.5, fontWeight: 800, padding: "2px 9px", borderRadius: 999, textTransform: "uppercase", letterSpacing: 0.5 },
  resTitle: { fontWeight: 700, fontSize: 14, color: "#fff", lineHeight: 1.3 },
  resGo: { fontWeight: 800, fontSize: 12, marginTop: 2 },
};

if (typeof document !== "undefined" && !document.getElementById("porte-kf")) {
  const st = document.createElement("style");
  st.id = "porte-kf";
  st.textContent = `@keyframes porteFloat{0%{transform:translateY(0) translateX(0);opacity:0}20%{opacity:.6}100%{transform:translateY(-90vh) translateX(var(--drift,0));opacity:0}}`;
  document.head.appendChild(st);
}
